"use client";

import * as React from "react";
import { cn } from "../cn";
import * as SwitchPrimitives from "@radix-ui/react-switch";

interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
  label?: string;
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, label, id: idProp, ...props }, ref) => {
  const generatedId = React.useId();
  const switchId = idProp ?? generatedId;

  const switchElement = (
    <SwitchPrimitives.Root
      id={switchId}
      className={cn(
        "peer",
        "inline-flex",
        "items-center",
        "data-[state=checked]:bg-primary",
        "data-[state=unchecked]:bg-input",
        "disabled:opacity-50",
        "shadow-sm",
        "rounded-full",
        "focus-visible:outline-none",
        "focus-visible:ring-2",
        "focus-visible:ring-ring",
        "focus-visible:ring-offset-2",
        "focus-visible:ring-offset-background",
        "w-10",
        "h-6",
        "transition-colors",
        "cursor-pointer",
        "bg-300",
        "border",
        "border-neutral",
        "hover:bg-gailPrimary/20",
        {
          "!bg-gailPrimary": props.checked,
        },
        !label && className
      )}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          "pointer-events-none",
          "block",
          "bg-white",
          "shadow-md",
          "rounded-full",
          "ring-0",
          "w-4",
          "h-4",
          "transition-transform",
          // w-10 track (40px) - w-4 thumb (16px) - ~2px inset each side => 20px travel
          "data-[state=checked]:translate-x-5",
          "data-[state=unchecked]:translate-x-0.5"
        )}
      />
    </SwitchPrimitives.Root>
  );

  if (label) {
    return (
      <div className={cn("flex", "items-center", "space-x-2", className)}>
        {switchElement}
        <label
          className={cn(
            "font-medium",
            "text-sm",
            "cursor-pointer",
            "mb-0",
            "peer-disabled:cursor-not-allowed",
            "peer-disabled:opacity-50"
          )}
          htmlFor={switchId}
        >
          {label}
        </label>
      </div>
    );
  }

  return switchElement;
});
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
